import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '../components/button.js'

/**
 * Граница ошибок.
 *
 * Нужна для того класса сбоев, который не ловится обычным способом: ошибка
 * при отрисовке. Без границы React снимает всё дерево, и человек видит белый
 * экран — из которого нельзя ни понять, что случилось, ни сообщить об этом.
 *
 * Классовый компонент здесь не наследие: componentDidCatch до сих пор не
 * имеет эквивалента среди хуков, и это единственное место в проекте, где
 * класс оправдан.
 */
interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Пока сборщик логов не подключён, консоль — единственное место,
    // где разработчик увидит стек вместе с деревом компонентов. Правило
    // `no-console` гонит к логгеру, а логгер у нас серверный и пишет
    // JSON — в браузере ему писать некуда, и здесь это исключение
    // осознанное, а не забытое.
    // eslint-disable-next-line no-console
    console.error('Ошибка отрисовки', error, info.componentStack)
  }

  override render(): ReactNode {
    const { error } = this.state

    if (!error) {
      return this.props.children
    }

    return (
      <div className="mx-auto flex min-h-dvh max-w-xl flex-col justify-center gap-4 p-8">
        <div>
          <h1 className="text-lg font-semibold text-ink">Интерфейс сломался</h1>
          <p className="mt-1 text-sm text-muted">
            Это ошибка в самой панели, а не в ваших данных. Ничего не потеряно: всё, что уже
            сохранено, осталось на сервере.
          </p>
        </div>

        <pre className="max-h-48 overflow-auto rounded-md border border-line bg-surface p-3 font-mono text-xs text-crimson">
          {error.message}
        </pre>

        <div className="flex gap-2">
          <Button variant="primary" onClick={() => window.location.reload()}>
            Обновить страницу
          </Button>
          <Button onClick={() => this.setState({ error: null })}>Попробовать ещё раз</Button>
        </div>
      </div>
    )
  }
}
